import { useEffect, useId, useState } from 'react';
import { Eye, EyeOff, KeyRound, RefreshCw, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { CopyButton } from './CopyButton';

export const NOT_RECOVERABLE_MESSAGE =
  'This secret cannot be recovered. Copy it now — you will not be able to view it again later.';

export interface SecretFieldProps {
  label: string;
  /** Revealed plaintext. When null/empty and not revealed, shows masked placeholder. */
  value?: string | null;
  maskedPlaceholder?: string;
  /** Controlled reveal state. If omitted, component manages local reveal when value is present. */
  revealed?: boolean;
  onReveal?: () => void | Promise<void>;
  onHide?: () => void;
  onRotate?: () => void;
  onRegenerate?: () => void;
  revealLoading?: boolean;
  rotateLabel?: string;
  regenerateLabel?: string;
  /** Show permanent non-recoverable warning (e.g. one-time issue). */
  notRecoverable?: boolean;
  message?: string | null;
  disabled?: boolean;
  className?: string;
  /** Called on unmount so parents can clear plaintext from memory. */
  onClearOnUnmount?: () => void;
  /** When true, value is shown immediately (e.g. just-created one-time secret). */
  initiallyRevealed?: boolean;
}

/**
 * Masked secret display with Reveal / Hide / Copy and optional Rotate / Regenerate.
 * Clears via `onClearOnUnmount` when the field leaves the tree.
 */
export function SecretField({
  label,
  value,
  maskedPlaceholder = '••••••••••••',
  revealed: revealedProp,
  onReveal,
  onHide,
  onRotate,
  onRegenerate,
  revealLoading = false,
  rotateLabel = 'Rotate',
  regenerateLabel = 'Regenerate',
  notRecoverable = false,
  message,
  disabled,
  className,
  onClearOnUnmount,
  initiallyRevealed = false,
}: SecretFieldProps) {
  const id = useId();
  const [localRevealed, setLocalRevealed] = useState(initiallyRevealed);
  const controlled = revealedProp !== undefined;
  const revealed = controlled ? revealedProp : localRevealed;
  const plaintext = value?.trim() ? value : '';
  const showPlain = revealed && !!plaintext;

  useEffect(() => {
    return () => {
      onClearOnUnmount?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReveal = async () => {
    if (onReveal) await onReveal();
    if (!controlled) setLocalRevealed(true);
  };

  const handleHide = () => {
    onHide?.();
    if (!controlled) setLocalRevealed(false);
  };

  return (
    <div className={cn('rounded-md border border-line px-3 py-3', className)}>
      <div className="flex items-center gap-2">
        <KeyRound size={14} aria-hidden="true" className="text-ink-muted" />
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">{label}</p>
      </div>

      <p
        id={id}
        className={cn(
          'mt-2 break-all font-mono text-base font-semibold tracking-wide text-ink',
          !showPlain && 'text-ink-muted',
        )}
      >
        {showPlain ? plaintext : maskedPlaceholder}
      </p>

      {notRecoverable && (
        <p role="status" className="mt-2 rounded-md border border-warning/30 bg-warning-soft px-2.5 py-1.5 text-[12px] text-warning">
          {NOT_RECOVERABLE_MESSAGE}
        </p>
      )}
      {message && <p className="mt-2 text-[13px] text-ink-secondary">{message}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {!showPlain ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled || revealLoading || (!onReveal && !plaintext)}
            loading={revealLoading}
            leftIcon={<Eye size={14} aria-hidden="true" />}
            onClick={() => void handleReveal()}
          >
            Reveal
          </Button>
        ) : (
          <>
            <CopyButton value={plaintext} size="sm" />
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<EyeOff size={14} aria-hidden="true" />}
              onClick={handleHide}
            >
              Hide
            </Button>
          </>
        )}
        {onRegenerate && (
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            leftIcon={<RefreshCw size={14} aria-hidden="true" />}
            onClick={onRegenerate}
          >
            {regenerateLabel}
          </Button>
        )}
        {onRotate && (
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            leftIcon={<RotateCcw size={14} aria-hidden="true" />}
            onClick={onRotate}
          >
            {rotateLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
